/**
 * @fileoverview miscellaneous formatting, display and clipboard utilities for solarIQ
 */

/*
------------------------------------------------------------------------------------------------------------
NUMBER FORMATTING
------------------------------------------------------------------------------------------------------------
*/

/**
 * fmtThou:
 * numbers are formatted to include thousand separators
 * includes a fallback to manual formatting in the case of Intl API unavailability
 * 
 * @param {number|string|null|undefined} n - number to format
 * @returns {string} - formatted number according to specifications OR empty string if invalid
 * 
 * @example 
 * fmtThou(1234567) // 1,234,567
 * fmtThou(null) // ""
 * fmtThou(abc) // ""
 */

export function fmtThou(n) {
    if (n === null || n === undefined) return "";  // no input
    const num = Number(n);
    if (isNaN(num)) return ""; // non-number input

    try {
        return num.toLocaleString("en-ZA");
    } catch {
        // fallback for older browsers: manual comma insertion
        return String(num).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
}

/**
 * fmtCurrencyK:
 * formats currency with Rand symbol and abbreviation for large amounts
 * abbreviates thousands (k) for values >= 1000
 * 
 * @param {number} n - amount in rands
 * @returns {string} - formatted currency string
 * 
 * @example
 * fmtCurrencyK(850)  //"R850"
 * fmtCurrencyK(35000)  //"R35k"
 * fmtCurrencyK(2500000)  //"R2500k"
 */
export function fmtCurrencyK(n) {
    if (n == null) return "R0";
    return n >= 1000
    ? "R" + (n / 1000).toFixed(0) + "k"
    : "R" + Math.round(n).toLocaleString();
}

/**
 * fmtCurrencyF:
 * formats currency with Rand symbol, showing full number
 * does not abbreviate - shows exact amount with thousand separators
 * 
 * @param {number} n - amount in rands
 * @returns {string} - formatted currency string
 * 
 * @example 
 * fmtCurrencyF(850) //"R850"
 * fmtCurrencyF(35000) //"R35,000"
 * fmtCurrencyF(2500000) //"R2,500,000"
 */
export function fmtCurrencyF(n) {
    if (n == null) return "R0";
    return "R" + Math.round(n).toLocaleString();
}

/*
------------------------------------------------------------------------------------------------------------
UTILITY HELPERS
------------------------------------------------------------------------------------------------------------
*/

/**
 * delay:
 * promise-based delay utility
 * useful for rate limiting, animations, sequential operations, etc.
 * 
 * @param {number} ms - milliseconds to wait
 * @returns {Promise<void>} - resolves after specified delay
 * 
 * @example
 * await delay(1000);  // wait one second
 * console.log("Waiting finished.");
 */
export function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/*
------------------------------------------------------------------------------------------------------------
CLIPBOARD UTILITIES
------------------------------------------------------------------------------------------------------------
*/

/**
 * safeCopy:
 * copies text to clipboard with automatic feedback, ensuring copying text works across all browsers
 * tries modern API first, then falls back to legacy method 
 * 
 * @param {string} text - text to copy
 * @returns {Promise<void>} - success: resolves, failure: rejects
 * 
 * @example
 * try {
 *   await safeCopy("Test Message");
 *   toast("Copied!");
 * } catch (err) {
 *   toast("Copy Failed.");
 * }
 * 
 */
export async function safeCopy(text) {
    // modern clipboard API (HTTPS + secure context required)
    if (navigator.clipboard && window.isSecureContext) {
        try {
            await navigator.clipboard.writeText(text);
            return;
        } 
        catch (err) {/* fall through */ }   // modern failed - fallback to legacy
    }
    // insecure context or older browser
    return fallbackCopy(text);
}

/**
 * fallbackCopy:
 * legacy clipboard copy using document.execCommand
 * creates invisible textarea, selects text, executes copy command
 * 
 * @param {string} text - text to copy
 * @returns {Promise<void>} - success: resolves, failure: rejects
 * @private
 */
function fallbackCopy(text) {
    return new Promise((resolve, reject) => {
        try {
            // invisible textarea creation
            const textArea = document.createElement("textarea");
            textArea.value = text;
            // hide offscreen (but still selectable)
            textArea.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0";
            // add to page and select text
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();        
            // deprecated copy command
            const ok = document.execCommand("copy");
            // remove from page
            document.body.removeChild(textArea);

            if (ok) {
                resolve();
            }
            else {
                reject(new Error("execCommand('copy') returned false"));
            }
        }

        catch (err) {
            reject(err);
        }
    });
}

